import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  SocialPlatform,
  SocialAnalyticsSnapshot,
  SocialPostAnalytics,
  SocialPost,
  AnalyticsSummary,
  AnalyticsTimeSeriesData,
  TopPost,
  UseSocialAnalyticsResult,
  SocialAccount
} from '@/types/social';
import { subDays, format, startOfDay, endOfDay } from 'date-fns';

const DEFAULT_DATE_RANGE_DAYS = 30;

export function useSocialAnalytics(businessId: string | null): UseSocialAnalyticsResult {
  const queryClient = useQueryClient();

  // State for filters
  const [dateRange, setDateRangeState] = useState<{ from: Date; to: Date }>({
    from: subDays(new Date(), DEFAULT_DATE_RANGE_DAYS),
    to: new Date()
  });
  const [selectedPlatforms, setSelectedPlatformsState] = useState<SocialPlatform[]>([]);

  const queryKey = ['social-analytics', businessId, dateRange, selectedPlatforms];

  // Fetch analytics data
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return null;

      // Fetch active accounts
      const { data: accounts, error: accountsError } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('business_id', businessId)
        .eq('status', 'active');

      if (accountsError) throw accountsError;

      // Filter accounts by selected platforms if any
      const filteredAccounts = selectedPlatforms.length > 0
        ? accounts?.filter(a => selectedPlatforms.includes(a.platform as SocialPlatform))
        : accounts;

      if (!filteredAccounts || filteredAccounts.length === 0) {
        return {
          summary: null,
          timeSeries: [],
          topPosts: [],
          accounts: []
        };
      }

      const accountIds = filteredAccounts.map(a => a.id);

      // Fetch analytics snapshots for date range
      const { data: snapshots, error: snapshotsError } = await supabase
        .from('social_analytics_snapshots')
        .select('*')
        .eq('business_id', businessId)
        .in('social_account_id', accountIds)
        .gte('snapshot_date', format(dateRange.from, 'yyyy-MM-dd'))
        .lte('snapshot_date', format(dateRange.to, 'yyyy-MM-dd'))
        .order('snapshot_date', { ascending: true });

      if (snapshotsError) throw snapshotsError;

      // Fetch posts with analytics for the date range
      const { data: posts, error: postsError } = await supabase
        .from('social_posts')
        .select(`
          *,
          social_post_analytics(*)
        `)
        .eq('business_id', businessId)
        .eq('status', 'published')
        .gte('published_at', dateRange.from.toISOString())
        .lte('published_at', dateRange.to.toISOString())
        .order('published_at', { ascending: false });

      if (postsError) throw postsError;

      // Calculate summary
      const summary = calculateSummary(snapshots as SocialAnalyticsSnapshot[], dateRange);

      // Build time series data
      const timeSeries = buildTimeSeries(snapshots as SocialAnalyticsSnapshot[], dateRange);

      // Get top posts
      const topPosts = getTopPosts(posts as any[], filteredAccounts as SocialAccount[]);

      return {
        summary,
        timeSeries,
        topPosts,
        accounts: filteredAccounts as SocialAccount[]
      };
    },
    enabled: !!businessId,
  });

  // Memoized values
  const summary = useMemo(() => data?.summary || null, [data]);
  const timeSeries = useMemo(() => data?.timeSeries || [], [data]);
  const topPosts = useMemo(() => data?.topPosts || [], [data]);

  // Setters
  const setDateRange = useCallback((from: Date, to: Date) => {
    setDateRangeState({ from, to });
  }, []);

  const setSelectedPlatforms = useCallback((platforms: SocialPlatform[]) => {
    setSelectedPlatformsState(platforms);
  }, []);

  const refresh = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    summary,
    timeSeries,
    topPosts,
    isLoading,
    error: error as Error | null,
    dateRange,
    setDateRange,
    selectedPlatforms,
    setSelectedPlatforms,
    refresh,
  };
}

// Calculate summary statistics
function calculateSummary(
  snapshots: SocialAnalyticsSnapshot[],
  dateRange: { from: Date; to: Date }
): AnalyticsSummary | null {
  if (!snapshots || snapshots.length === 0) {
    return {
      total_followers: 0,
      followers_change: 0,
      followers_change_percent: 0,
      total_engagement: 0,
      engagement_change: 0,
      engagement_change_percent: 0,
      total_posts: 0,
      avg_engagement_rate: 0,
      total_messages_received: 0,
      total_messages_sent: 0,
    };
  }

  // Get latest snapshot per account
  const latestByAccount: Record<string, SocialAnalyticsSnapshot> = {};
  const earliestByAccount: Record<string, SocialAnalyticsSnapshot> = {};

  snapshots.forEach(snapshot => {
    const accountId = snapshot.social_account_id;

    if (!latestByAccount[accountId] ||
        new Date(snapshot.snapshot_date) > new Date(latestByAccount[accountId].snapshot_date)) {
      latestByAccount[accountId] = snapshot;
    }

    if (!earliestByAccount[accountId] ||
        new Date(snapshot.snapshot_date) < new Date(earliestByAccount[accountId].snapshot_date)) {
      earliestByAccount[accountId] = snapshot;
    }
  });

  // Calculate totals from latest snapshots
  const latestSnapshots = Object.values(latestByAccount);
  const earliestSnapshots = Object.values(earliestByAccount);

  const totalFollowers = latestSnapshots.reduce((sum, s) => sum + (s.followers_count || 0), 0);
  const earlierFollowers = earliestSnapshots.reduce((sum, s) => sum + (s.followers_count || 0), 0);
  const followersChange = totalFollowers - earlierFollowers;
  const followersChangePercent = earlierFollowers > 0
    ? (followersChange / earlierFollowers) * 100
    : 0;

  // Calculate engagement totals
  const totalEngagement = snapshots.reduce((sum, s) =>
    sum + (s.likes_count || 0) + (s.comments_count || 0) + (s.shares_count || 0) + (s.saves_count || 0), 0);

  // Calculate engagement for first half vs second half of period
  const midDate = new Date((dateRange.from.getTime() + dateRange.to.getTime()) / 2);
  const firstHalfEngagement = snapshots
    .filter(s => new Date(s.snapshot_date) < midDate)
    .reduce((sum, s) =>
      sum + (s.likes_count || 0) + (s.comments_count || 0) + (s.shares_count || 0) + (s.saves_count || 0), 0);
  const secondHalfEngagement = snapshots
    .filter(s => new Date(s.snapshot_date) >= midDate)
    .reduce((sum, s) =>
      sum + (s.likes_count || 0) + (s.comments_count || 0) + (s.shares_count || 0) + (s.saves_count || 0), 0);

  const engagementChange = secondHalfEngagement - firstHalfEngagement;
  const engagementChangePercent = firstHalfEngagement > 0
    ? (engagementChange / firstHalfEngagement) * 100
    : 0;

  const totalPosts = snapshots.reduce((sum, s) => sum + (s.posts_published || 0), 0);
  const avgEngagementRate = latestSnapshots.length > 0
    ? latestSnapshots.reduce((sum, s) => sum + (s.engagement_rate || 0), 0) / latestSnapshots.length
    : 0;

  const totalMessagesReceived = snapshots.reduce((sum, s) => sum + (s.messages_received || 0), 0);
  const totalMessagesSent = snapshots.reduce((sum, s) => sum + (s.messages_sent || 0), 0);

  return {
    total_followers: totalFollowers,
    followers_change: followersChange,
    followers_change_percent: Math.round(followersChangePercent * 10) / 10,
    total_engagement: totalEngagement,
    engagement_change: engagementChange,
    engagement_change_percent: Math.round(engagementChangePercent * 10) / 10,
    total_posts: totalPosts,
    avg_engagement_rate: Math.round(avgEngagementRate * 100) / 100,
    total_messages_received: totalMessagesReceived,
    total_messages_sent: totalMessagesSent,
  };
}

// Build time series data for charts
function buildTimeSeries(
  snapshots: SocialAnalyticsSnapshot[],
  dateRange: { from: Date; to: Date }
): AnalyticsTimeSeriesData[] {
  if (!snapshots || snapshots.length === 0) return [];

  // Group snapshots by date
  const byDate: Record<string, SocialAnalyticsSnapshot[]> = {};

  snapshots.forEach(snapshot => {
    const dateKey = snapshot.snapshot_date;
    if (!byDate[dateKey]) {
      byDate[dateKey] = [];
    }
    byDate[dateKey].push(snapshot);
  });

  // Build time series
  const timeSeries: AnalyticsTimeSeriesData[] = Object.entries(byDate)
    .map(([date, daySnapshots]) => ({
      date,
      followers: daySnapshots.reduce((sum, s) => sum + (s.followers_count || 0), 0),
      engagement: daySnapshots.reduce((sum, s) =>
        sum + (s.likes_count || 0) + (s.comments_count || 0) + (s.shares_count || 0) + (s.saves_count || 0), 0),
      impressions: daySnapshots.reduce((sum, s) => sum + (s.impressions || 0), 0),
      reach: daySnapshots.reduce((sum, s) => sum + (s.reach || 0), 0),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return timeSeries;
}

// Get top performing posts
function getTopPosts(
  posts: Array<SocialPost & { social_post_analytics: SocialPostAnalytics[] }>,
  accounts: SocialAccount[]
): TopPost[] {
  if (!posts || posts.length === 0) return [];

  const topPosts: TopPost[] = [];

  posts.forEach(post => {
    if (!post.social_post_analytics || post.social_post_analytics.length === 0) return;

    post.social_post_analytics.forEach(analytics => {
      const totalEngagement =
        (analytics.likes_count || 0) +
        (analytics.comments_count || 0) +
        (analytics.shares_count || 0) +
        (analytics.saves_count || 0);

      topPosts.push({
        post,
        analytics,
        platform: analytics.platform,
      });
    });
  });

  // Sort by total engagement and take top 10
  return topPosts
    .sort((a, b) => {
      const aEngagement = (a.analytics.likes_count || 0) + (a.analytics.comments_count || 0) +
        (a.analytics.shares_count || 0) + (a.analytics.saves_count || 0);
      const bEngagement = (b.analytics.likes_count || 0) + (b.analytics.comments_count || 0) +
        (b.analytics.shares_count || 0) + (b.analytics.saves_count || 0);
      return bEngagement - aEngagement;
    })
    .slice(0, 10);
}

export default useSocialAnalytics;
